import { useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import isEmptyString from '@framework/data-helpers/string/isEmptyString';
import { getAdditionalParams } from '@/creditflowresearch/application/helpers';
import { RESEARCH_STORE_NAME, INITIAL_FILTER_STATE } from '@/creditflowresearch/application/Research/constants';
import { IResearchFilterState, IResearchFilterItems } from '@/creditflowresearch/domain/vo/research/IFilterContextData';

interface IUseFilterProps {
  initialFilters: IResearchFilterState,
  setAppliedFilters: (filters: IResearchFilterState) => void,
  setFiltersPayload: (payload: IResearchFilterState) => void,
  setMinMaxDatePeriod: (dateFrom: number, dateTo: number) => void,
  getResearchFilter: (filtersPayload: IResearchFilterState) => Promise<IResearchFilterItems>,
  removeFilterParamsFromUrl: () => void,
  updateUrlWithParams: (params: Record<string, any>) => void,
}

export const useFilter = ({
  initialFilters,
  setAppliedFilters,
  setFiltersPayload,
  setMinMaxDatePeriod,
  getResearchFilter,
  removeFilterParamsFromUrl,
  updateUrlWithParams,
}: IUseFilterProps) => {
  const getResearchFiltersInitialState = useCallback((): IResearchFilterState => INITIAL_FILTER_STATE, []);

  const getFiltersPayload = useCallback((filters: IResearchFilterState = INITIAL_FILTER_STATE) => () => filters, []);

  const applyFilter = useCallback(async (filters: IResearchFilterState) => {
    const filtersPayload = getFiltersPayload(filters)();
    const isInitialFilters = isEqual(initialFilters, filters);
    const appliedFilters = isInitialFilters ? INITIAL_FILTER_STATE : filters;

    setFiltersPayload(filtersPayload);
    setAppliedFilters(appliedFilters);

    if (isInitialFilters) {
      removeFilterParamsFromUrl();
      return;
    }

    const additionalParams = getAdditionalParams(filtersPayload);
    if (Object.keys(additionalParams).length > 0) {
      updateUrlWithParams(additionalParams);
    }
  }, [
    initialFilters,
    getFiltersPayload,
    setFiltersPayload,
    setAppliedFilters,
    removeFilterParamsFromUrl,
    updateUrlWithParams,
  ]);

  const fetchFilter = useCallback(async (filters: IResearchFilterState) => {
    const filtersPayload = getFiltersPayload(filters)();
    const response = await getResearchFilter(filtersPayload);

    const { filter: researchFilter = INITIAL_FILTER_STATE, hash = '' } = response;
    const isInitialFilters = isEqual(initialFilters, filters);
    const isHash = isEmptyString(hash);

    if (researchFilter.dateFrom && researchFilter.dateTo) {
      setMinMaxDatePeriod(researchFilter.dateFrom, researchFilter.dateTo);
    }

    if (!isHash && !isInitialFilters) {
      const additionalParams = getAdditionalParams(filtersPayload);
      updateUrlWithParams({ hash, ...additionalParams });
    }
  }, [getFiltersPayload, getResearchFilter, initialFilters, setMinMaxDatePeriod, updateUrlWithParams]);

  const initFilters = useCallback(async (
    filtersPayload: IResearchFilterState,
    filtersState: IResearchFilterState = getResearchFiltersInitialState(),
    isHashPayload: boolean = false,
    filtersUpdate?: (storeName: string, filters: IResearchFilterState) => void,
  ) => {
    const response = await getResearchFilter(filtersPayload);
    const { filter: researchFilter = INITIAL_FILTER_STATE } = response;

    const filtersToApply = isHashPayload ? filtersPayload : filtersState;
    const storeName = RESEARCH_STORE_NAME;

    if (filtersUpdate) {
      filtersUpdate(storeName, filtersToApply);
    }

    setAppliedFilters(filtersToApply);

    if (researchFilter.dateFrom && researchFilter.dateTo) {
      setMinMaxDatePeriod(researchFilter.dateFrom, researchFilter.dateTo);
    }
  }, [getResearchFilter, setAppliedFilters, setMinMaxDatePeriod, getResearchFiltersInitialState]);

  const resetFilters = useCallback((filtersUpdate?: (storeName: string, filters: IResearchFilterState) => void) => {
    const initialState = getResearchFiltersInitialState();
    const filtersPayload = getFiltersPayload(initialState)();

    setFiltersPayload(filtersPayload);
    initFilters(filtersPayload, initialState, false, filtersUpdate);
    setAppliedFilters(filtersPayload);
    removeFilterParamsFromUrl();
  }, [
    getResearchFiltersInitialState,
    getFiltersPayload,
    setFiltersPayload,
    initFilters,
    setAppliedFilters,
    removeFilterParamsFromUrl,
  ]);

  return {
    getResearchFiltersInitialState,
    getFiltersPayload,
    applyFilter,
    fetchFilter,
    initFilters,
    resetFilters,
  };
};
